/** Shared flyer rules for the admin UI and the API Worker. */

export const FLYER_TARGET_BYTES = 1024 * 1024;
/** Server rejects anything larger. The browser compresses toward {@link FLYER_TARGET_BYTES}. */
export const FLYER_MAX_BYTES = Math.round(1.5 * 1024 * 1024);
/** Originals above this are rejected before the browser tries to compress them. */
export const FLYER_SOURCE_MAX_BYTES = 20 * 1024 * 1024;

export const FLYER_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type FlyerMime = (typeof FLYER_MIME_TYPES)[number];

export const FLYER_CACHE_CONTROL = 'public, max-age=31536000';

const STORED_IMAGE_RE = /^\/api\/public\/events\/([a-z0-9]+(?:-[a-z0-9]+)*)\/(flyer|card-image)$/;

export type StoredEventImageKind = 'flyer' | 'card-image';

export function eventFlyerKvKey(eventId: string): string {
  return `eventflyer:${eventId}`;
}

export function eventCardImageKvKey(eventId: string): string {
  return `eventcard:${eventId}`;
}

export function eventFlyerPath(slug: string): string {
  return `/api/public/events/${slug}/flyer`;
}

export function eventCardImagePath(slug: string): string {
  return `/api/public/events/${slug}/card-image`;
}

export function stripEventImageQuery(image: string): string {
  const query = image.indexOf('?');
  return query === -1 ? image : image.slice(0, query);
}

export function eventImageVersion(image: string | null | undefined): string | null {
  if (!image) return null;
  const match = image.match(/[?&]v=([a-z0-9]+)/i);
  return match?.[1] ?? null;
}

export function parseStoredEventImage(
  image: string | null | undefined,
): { slug: string; kind: StoredEventImageKind; version: string | null } | null {
  if (typeof image !== 'string') return null;
  const trimmed = image.trim();
  if (!trimmed) return null;
  const match = STORED_IMAGE_RE.exec(stripEventImageQuery(trimmed));
  if (!match) return null;
  const kind = match[2] === 'card-image' ? 'card-image' : 'flyer';
  return { slug: match[1]!, kind, version: eventImageVersion(trimmed) };
}

export function isEventFlyerPath(image: string | null | undefined): boolean {
  return parseStoredEventImage(image)?.kind === 'flyer';
}

export function isEventCardImagePath(image: string | null | undefined): boolean {
  return parseStoredEventImage(image)?.kind === 'card-image';
}

/** Eight hex characters. Opaque to clients. */
export function shortImageVersion(seed: string): string {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0').slice(0, 8);
}

export function freshImageVersion(): string {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function withImageVersion(path: string, version: string): string {
  return `${stripEventImageQuery(path)}?v=${version}`;
}

/**
 * API output for `image` and `cardImage`.
 * Stored flyer and card paths gain `?v=` so a replacement is a new URL.
 * Static and legacy values, such as a rifle-shooting .jpeg URL, are returned unchanged.
 */
export function presentEventImage(image: string | null | undefined, versionSeed: string): string | null {
  const trimmed = image?.trim() ?? '';
  if (!trimmed) return null;
  const stored = parseStoredEventImage(trimmed);
  if (!stored) return trimmed;
  const path = stored.kind === 'card-image' ? eventCardImagePath(stored.slug) : eventFlyerPath(stored.slug);
  return withImageVersion(path, stored.version ?? shortImageVersion(versionSeed));
}

/**
 * `<img src>` for the member event details view. API-relative flyer paths are loaded
 * from the API host (`getApiBaseUrl()` / `apiUrl`). Absolute http(s) URLs stay as stored.
 */
export function resolveEventImageSrc(
  image: string | null | undefined,
  apiBase: string,
): string | null {
  const trimmed = image?.trim() ?? '';
  if (!trimmed) return null;
  if (!isEventFlyerPath(trimmed)) return trimmed;
  const base = apiBase.replace(/\/$/, '');
  return base ? `${base}${trimmed}` : trimmed;
}

export function normalizeFlyerMime(type: string | null | undefined): FlyerMime | null {
  const bare = (type ?? '').toLowerCase().split(';')[0]?.trim() ?? '';
  if (bare === 'image/jpg' || bare === 'image/pjpeg') return 'image/jpeg';
  if ((FLYER_MIME_TYPES as readonly string[]).includes(bare)) return bare as FlyerMime;
  return null;
}

export function flyerMimeFromName(name: string): FlyerMime | null {
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  return null;
}

/** JPEG, PNG, or WebP from magic bytes. Declared Content-Type is not trusted. */
export function sniffFlyerMime(bytes: Uint8Array): FlyerMime | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return 'image/png';
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp';
  }
  return null;
}

export interface FlyerSelection {
  type: string;
  name: string;
  size: number;
}

/**
 * Client-side check before compression. Returns a message the admin can act on,
 * or null when the file is worth compressing / uploading.
 */
export function flyerSelectionError(file: FlyerSelection): string | null {
  const declared = file.type.trim() ? normalizeFlyerMime(file.type) : flyerMimeFromName(file.name);
  if (!declared) return 'Upload a JPEG, PNG, or WebP image.';
  if (file.size <= 0) return 'That file is empty. Choose a flyer image.';
  if (file.size > FLYER_SOURCE_MAX_BYTES) {
    return 'This image is too large. Choose a flyer under 20 MB.';
  }
  return null;
}

export interface FlyerWritePlan {
  /** Undefined when `events.image` should stay as it is. */
  image?: string | null;
  /** Drop the KV object. Never set together with a new upload. */
  deleteStored: boolean;
}

/**
 * Decide how create/update should change `events.image` and the KV object.
 * A file upload wins over an `image` string. URL strings (seeded static files) stay supported.
 * The public flyer path is only stored after an upload, or rewritten when a stored flyer’s slug changes.
 */
export function planFlyerWrite(input: {
  previousImage: string | null;
  nextSlug: string;
  /** Undefined when the client did not send `image`. */
  requestedImage: string | null | undefined;
  hasUpload: boolean;
}): { ok: true; plan: FlyerWritePlan } | { ok: false; error: string } {
  return planStoredImageWrite('flyer', input);
}

export function planCardImageWrite(input: {
  previousImage: string | null;
  nextSlug: string;
  /** Undefined when the client did not send `cardImage`. */
  requestedImage: string | null | undefined;
  hasUpload: boolean;
}): { ok: true; plan: FlyerWritePlan } | { ok: false; error: string } {
  return planStoredImageWrite('card-image', input);
}

function planStoredImageWrite(
  kind: StoredEventImageKind,
  input: {
    previousImage: string | null;
    nextSlug: string;
    requestedImage: string | null | undefined;
    hasUpload: boolean;
  },
): { ok: true; plan: FlyerWritePlan } | { ok: false; error: string } {
  const pathFor = kind === 'card-image' ? eventCardImagePath : eventFlyerPath;
  const matches = (image: string | null | undefined) => parseStoredEventImage(image)?.kind === kind;
  if (input.hasUpload) {
    return { ok: true, plan: { image: pathFor(input.nextSlug), deleteStored: false } };
  }

  if (input.requestedImage === null) {
    return { ok: true, plan: { image: null, deleteStored: true } };
  }

  if (typeof input.requestedImage === 'string') {
    if (matches(input.requestedImage)) {
      if (!matches(input.previousImage)) {
        const label = kind === 'card-image' ? 'card image' : 'flyer';
        return { ok: false, error: `Upload a ${label} file instead of setting the ${label} path.` };
      }
      const next = stampedPath(pathFor(input.nextSlug), input.previousImage);
      return {
        ok: true,
        plan: {
          image: input.previousImage === next ? undefined : next,
          deleteStored: false,
        },
      };
    }
    return { ok: true, plan: { image: input.requestedImage, deleteStored: true } };
  }

  if (matches(input.previousImage)) {
    const next = stampedPath(pathFor(input.nextSlug), input.previousImage);
    if (input.previousImage !== next) {
      return { ok: true, plan: { image: next, deleteStored: false } };
    }
  }

  return { ok: true, plan: { deleteStored: false } };
}

function stampedPath(path: string, previousImage: string | null): string {
  const version = eventImageVersion(previousImage);
  return version ? withImageVersion(path, version) : path;
}

/** Public flyer bytes are served only for events that appear on `/api/public/events`. */
export function isPublicFlyerVisible(event: {
  visibility: string;
  publishState: string;
}): boolean {
  return event.visibility === 'public' && event.publishState !== 'draft';
}
